/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { storyblokEditable } from '@storyblok/react/rsc'
import EmblaAutoScroll from 'embla-carousel-auto-scroll'
import useEmblaCarousel from 'embla-carousel-react'
import React from 'react'

import { Image } from '@/components/ui/Image'
import { InfiniteLogoMarqueeStoryblok } from '@/lib/storyblok/types/generated-types'
import { getAssetImageDimensions } from '@/lib/storyblok/utils/image-asset'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type InfiniteLogoMarqueeProps = {
  /** The Storyblok blok that represents this component. */
  blok: InfiniteLogoMarqueeStoryblok
}

/**
 * A Storyblok component for displaying an infinite scrolling banner that displays logos with optional purple tint and
 * edge fading effects.
 */
const InfiniteLogoMarquee: React.FC<InfiniteLogoMarqueeProps> = ({ blok }) => {
  const { logos, enable_tint, enable_fade } = blok
  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
    },
    [
      EmblaAutoScroll({
        playOnInit: true,
      }),
    ],
  )

  return (
    <div className={'relative w-full py-8'} {...storyblokEditable(blok)}>
      <div className={cn('relative w-full overflow-hidden', enable_fade && 'mask-logo-fade')} ref={emblaRef}>
        <div className={'flex'}>
          {logos.map((logo) => (
            <div key={logo.id} className={'relative flex-none w-40 pl-8 flex items-center justify-center'}>
              <Image
                src={logo.filename}
                alt={logo.alt ?? ''}
                className={cn('relative h-20 w-auto object-contain', enable_tint && 'filter-purple')}
                {...getAssetImageDimensions(logo)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export { InfiniteLogoMarquee }
